import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ArrowDownLeft, ArrowUpRight, HelpCircle, Inbox, Loader2, Mail, Paperclip, PlugZap, Search, Settings2, Users } from "lucide-react";
import { StatusBadge, UnlinkedEmails, formatDateTime } from "./MailboxSettings";
import { ConnectionTab, TeamMailboxesTab, SetupGuideTab } from "@/pages/settings/EmailSettingsPage";
import type { EmailCounts, InboxPage, MailboxInfo } from "@/types/email";

type TabKey = "inbox" | "unlinked" | "settings" | "team" | "guide";
type FilterKey = "all" | "unread" | "received" | "sent";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "received", label: "Received" },
  { key: "sent", label: "Sent" },
];

const DEAL_TAG_RE = /\s*\[Deal #[0-9a-f]{8}\]/i;
const cleanSubject = (s: string) => (s || "(no subject)").replace(DEAL_TAG_RE, "").trim() || "(no subject)";

export function EmailsPage() {
  const { hasPermission, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const canSend = hasPermission("email.send");
  const canSeeTeam = hasPermission("mailbox.admin");
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();

  const rawRequested = (params.get("tab") || "inbox") as string;
  const normalizedRequested: TabKey =
    rawRequested === "connection" ? "settings" : (rawRequested as TabKey);
  const tab: TabKey =
    (normalizedRequested === "settings" && !canSend) ||
    (normalizedRequested === "team" && !canSeeTeam) ||
    (normalizedRequested === "guide" && !canSend)
      ? "inbox"
      : normalizedRequested;
  const setTab = (key: TabKey) => setParams(key === "inbox" ? {} : { tab: key }, { replace: true });

  const [mailbox, setMailbox] = useState<MailboxInfo | null>(null);
  const [mailboxLoaded, setMailboxLoaded] = useState(false);
  const [counts, setCounts] = useState<EmailCounts>({ unread: 0, unlinked: 0 });

  const loadCounts = useCallback(() => {
    api
      .get("/api/DealEmail/Counts")
      .then((res) => setCounts(unwrapObject<EmailCounts>(res, { unread: 0, unlinked: 0 })))
      .catch(() => undefined);
  }, []);

  useEffect(loadCounts, [loadCounts]);

  const loadMailbox = useCallback(() => {
    if (!canSend) return setMailboxLoaded(true);
    api
      .get("/api/Mailbox/Me")
      .then((res) => setMailbox(unwrapObject<{ mailbox: MailboxInfo | null }>(res)?.mailbox || null))
      .catch(() => setMailbox(null))
      .finally(() => setMailboxLoaded(true));
  }, [canSend]);

  useEffect(() => {
    loadMailbox();
  }, [loadMailbox]);

  const scopeHint =
    roleCode === "SUPER_ADMIN"
      ? "Emails on every deal in the company."
      : roleCode === "SALES_HEAD"
        ? "Emails on your deals and your team's deals."
        : "Emails on your deals.";

  const tabs: { key: TabKey; label: string; icon: React.ElementType; badge?: number; show: boolean }[] = [
    { key: "inbox", label: "Inbox", icon: Inbox, badge: counts.unread, show: true },
    { key: "unlinked", label: "Needs a deal", icon: Mail, badge: counts.unlinked, show: true },
    { key: "settings", label: "Email Connection", icon: PlugZap, show: canSend },
    { key: "team", label: roleCode === "SALES_HEAD" ? "Team Mailboxes" : "All Mailboxes", icon: Users, show: canSeeTeam },
    { key: "guide", label: "Setup Guide", icon: HelpCircle, show: canSend },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-primary/10 text-primary">
            <Mail className="size-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Emails</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{scopeHint}</p>
          </div>
        </div>
        {canSend && mailboxLoaded && (
          <button
            type="button"
            onClick={() => setTab("settings")}
            className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-left hover:bg-muted/50 cursor-pointer"
          >
            <div className="text-xs">
              <div className="font-semibold text-foreground">{mailbox?.email_address || "Mailbox not connected"}</div>
              <div className="text-muted-foreground">Your sending address</div>
            </div>
            <StatusBadge mailbox={mailbox} />
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap cursor-pointer",
                tab === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <t.icon className="size-4" />
              {t.label}
              {!!t.badge && (
                <span className="text-[10px] font-bold px-1.5 rounded-full bg-primary text-primary-foreground">{t.badge}</span>
              )}
            </button>
          ))}
      </div>

      {tab === "inbox" && (
        <>
          {canSend && mailboxLoaded && (!mailbox || mailbox.sync_status === "AUTH_FAILED") && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
              <span>
                {mailbox
                  ? "Your mailbox login failed, so new replies are not coming in."
                  : "Connect your mailbox to send emails from deals and receive customer replies here."}
              </span>
              <Button size="sm" onClick={() => setTab("settings")} className="cursor-pointer">
                {mailbox ? "Reconnect" : "Connect Mailbox"}
              </Button>
            </div>
          )}
          <InboxList onRead={loadCounts} />
        </>
      )}
      {tab === "unlinked" && <UnlinkedEmails onChanged={loadCounts} />}
      {tab === "settings" && canSend && (
        <ConnectionTab mailbox={mailbox} onUpdated={loadMailbox} />
      )}
      {tab === "team" && canSeeTeam && <TeamMailboxesTab />}
      {tab === "guide" && canSend && (
        <SetupGuideTab onSelectPreset={() => setTab("settings")} />
      )}
    </div>
  );
}

/** Emails across all deals in the user's scope; opening one goes to its deal */
function InboxList({ onRead }: { onRead: () => void }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<InboxPage | null>(null);
  const [loading, setLoading] = useState(true);

  // Search after the user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ filter, page: String(page) });
    if (query) qs.set("search", query);
    api
      .get(`/api/DealEmail/List?${qs.toString()}`)
      .then((res) => setData(unwrapObject<InboxPage>(res, { rows: [], total: 0, page: 1, page_size: 30 })))
      .catch(() => setData({ rows: [], total: 0, page: 1, page_size: 30 }))
      .finally(() => setLoading(false));
  }, [filter, query, page]);

  const open = async (dealId: string, unread: boolean) => {
    if (unread) {
      await api.patch(`/api/DealEmail/MarkDealRead/${dealId}`).catch(() => undefined);
      onRead();
    }
    navigate(`/deals/${dealId}`);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b">
        <div className="flex items-center p-1 rounded-lg border bg-background">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                setFilter(f.key);
                setPage(1);
              }}
              className={cn(
                "px-3 py-1 text-xs font-medium rounded-md",
                filter === f.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-72">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject, sender, deal"
            className="pl-10 h-9 text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          {query || filter !== "all"
            ? "No emails match."
            : "No emails yet. Send one from a deal's Emails card, and customer replies will show up here."}
        </div>
      ) : (
        <ul className="divide-y">
          {data.rows.map((e) => {
            const unread = e.direction === "IN" && !e.is_read;
            const who =
              e.direction === "IN"
                ? e.from_name || e.from_address
                : `To ${e.to_addresses.join(", ")}`;
            const owner = e.user ? `${e.user.first_name} ${e.user.last_name || ""}`.trim() : "";
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => open(e.deal.id, unread)}
                  className={cn("w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/50", unread && "bg-primary/5")}
                >
                  {e.direction === "IN" ? (
                    <ArrowDownLeft className="size-4 mt-0.5 shrink-0 text-emerald-600" />
                  ) : (
                    <ArrowUpRight className="size-4 mt-0.5 shrink-0 text-primary" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm truncate", unread ? "font-bold" : "font-medium")}>{who}</span>
                      {unread && <span className="size-2 rounded-full bg-primary shrink-0" />}
                    </div>
                    <div className={cn("text-sm truncate", unread ? "text-foreground" : "text-foreground/80")}>
                      {cleanSubject(e.subject)}
                      {e.snippet && <span className="text-muted-foreground"> — {e.snippet}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                      <span className="px-1.5 py-0.5 rounded bg-muted text-foreground/80 truncate max-w-[16rem]">{e.deal.name}</span>
                      {owner && <span>Mailbox: {owner}</span>}
                      {e.attachments.length > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Paperclip className="size-3" />
                          {e.attachments.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">{formatDateTime(e.email_date)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t text-xs text-muted-foreground">
          <span>
            {data.total} emails · page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailsPage;
